import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { siteSwitches } from "../../api/endpoints/siteSwitches";
import { qk } from "../../api/queryKeys";
import type {
  ManagementMode,
  ProvisioningStrategy,
  SwitchFields,
  SwitchIn,
  SwitchOut,
  SwitchUpdateIn,
} from "../../api/types/siteSwitch";
import { FormShell } from "../../components/forms/FormShell";
import { TextField } from "../../components/forms/TextField";
import { SelectField } from "../../components/forms/SelectField";
import { NumberField } from "../../components/forms/NumberField";
import { CheckboxField } from "../../components/forms/CheckboxField";
import { StringListField } from "../../components/forms/StringListField";
import { useVlanTemplateOptions } from "./useVlanTemplateOptions";
import { getManagementMode, withManagementMode } from "./managementMode";
import { vaultFieldRefError } from "../../lib/fieldValidators";

const MODE_OPTIONS = [
  { value: "cli", label: "CLI (Hub configures the switch)" },
  { value: "web_ui", label: "Web UI (manual — Hub generates instructions)" },
] as const;

const STRATEGY_OPTIONS = [
  { value: "", label: "— none —" },
  { value: "fresh_build", label: "Fresh build" },
  { value: "migrate_in_place", label: "Migrate in place" },
] as const;

interface SwitchDraft {
  slug: string; // create-only
  management_mode: ManagementMode;
  identity: string;
  model: string;
  role: string;
  management_vlan_tag: number | null;
  uplink_port: number | null;
  maintenance_port: number | null;
  target_vlan_name: string;
  notes: string[];
  vendor_extras_json: string; // excludes management_mode (owned by the toggle)
  // CLI-only
  bootstrap_username: string;
  bootstrap_secret_ref: string;
  bootstrap_transport: string;
  bootstrap_port: number | null;
  mgmt_bootstrap_host: string;
  bootstrap_mask: string;
  bootstrap_gateway: string;
  ops_secret_ref: string;
  managed_host: string;
  managed_transport: string;
  managed_mask: string;
  managed_port: number | null;
  current_interface: string;
  provisioning_strategy: "" | ProvisioningStrategy;
  preserve_until_verified: boolean;
  allow_firmware_upgrade_before_backup: boolean;
  backup_local_capture_dir: string;
  discovery_json: string;
}

export function SwitchForm({
  siteSlug,
  sw,
  canWrite,
  onClose,
}: {
  siteSlug: string;
  sw?: SwitchOut;
  canWrite: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const vlan = useVlanTemplateOptions();
  const [draft, setDraft] = useState<SwitchDraft>(() => toDraft(sw));
  const set = <K extends keyof SwitchDraft>(k: K, v: SwitchDraft[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));

  const save = useMutation({
    mutationFn: (body: SwitchIn | SwitchUpdateIn) =>
      sw
        ? siteSwitches.update(siteSlug, sw.slug, body as SwitchUpdateIn)
        : siteSwitches.create(siteSlug, body as SwitchIn),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.siteSwitches(siteSlug) });
      if (sw) void qc.invalidateQueries({ queryKey: qk.siteSwitch(siteSlug, sw.slug) });
      onClose();
    },
  });

  const isCli = draft.management_mode === "cli";
  const vendor = parseJsonObject(draft.vendor_extras_json);
  const discovery = parseJsonObject(draft.discovery_json);
  const secretErrors = {
    bootstrap_secret_ref: vaultFieldRefError(draft.bootstrap_secret_ref),
    ops_secret_ref: vaultFieldRefError(draft.ops_secret_ref),
  };
  const slugError = !sw && draft.slug.trim() === "" ? "Required." : null;
  const isValid =
    !slugError &&
    vendor.ok &&
    discovery.ok &&
    !secretErrors.bootstrap_secret_ref &&
    !secretErrors.ops_secret_ref;
  const disabled = !canWrite || save.isPending;

  const vlanOptions = [
    { value: "", label: "— none —" },
    ...vlan.options.map((o) => ({ value: String(o.value), label: o.label })),
  ];

  return (
    <FormShell
      submitLabel={sw ? "Save switch" : "Add switch"}
      onSubmit={(e) => {
        e.preventDefault();
        if (!isValid || disabled) return;
        const fields = fromDraft(
          draft,
          vendor.ok ? vendor.value : {},
          discovery.ok ? discovery.value : {},
        );
        save.mutate(sw ? fields : { ...fields, slug: draft.slug.trim() });
      }}
      onCancel={onClose}
      pending={save.isPending}
      submitDisabled={!canWrite || !isValid}
      error={save.error}
      errorTitle="Switch save failed"
    >
      <SelectField
        label="Management mode"
        value={draft.management_mode}
        onChange={(v) => set("management_mode", v as ManagementMode)}
        options={MODE_OPTIONS}
        helper={
          isCli
            ? "Hub will configure this switch over its CLI (applied in a later phase)."
            : "Hub can't drive this switch — it generates setup instructions you apply in the device's web UI."
        }
        disabled={disabled}
      />

      {sw ? (
        <p style={{ fontSize: "0.85rem", color: "var(--text-dim)" }}>
          Slug: <code>{sw.slug}</code> (immutable)
        </p>
      ) : (
        <TextField
          label="Slug"
          value={draft.slug}
          onChange={(v) => set("slug", v)}
          placeholder="core-switch-01"
          error={slugError}
          disabled={disabled}
        />
      )}

      <TextField label="Identity" value={draft.identity} onChange={(v) => set("identity", v)} placeholder="abdullah-raya-sw-01" disabled={disabled} />
      <TextField label="Model" value={draft.model} onChange={(v) => set("model", v)} placeholder="AT-9000/28" disabled={disabled} />
      <TextField label="Role" value={draft.role} onChange={(v) => set("role", v)} placeholder="core / access" disabled={disabled} />
      <SelectField
        label="Management VLAN"
        value={draft.management_vlan_tag == null ? "" : String(draft.management_vlan_tag)}
        onChange={(v) => set("management_vlan_tag", v === "" ? null : Number(v))}
        options={vlanOptions}
        disabled={disabled}
      />
      <NumberField label="Uplink port" value={draft.uplink_port} onChange={(v) => set("uplink_port", v)} min={1} disabled={disabled} />
      <NumberField label="Maintenance port" value={draft.maintenance_port} onChange={(v) => set("maintenance_port", v)} min={1} disabled={disabled} />
      <TextField label="Target VLAN name" value={draft.target_vlan_name} onChange={(v) => set("target_vlan_name", v)} disabled={disabled} />

      {isCli && (
        <>
          <SectionLabel>Bootstrap (factory) access</SectionLabel>
          <TextField label="Bootstrap username" value={draft.bootstrap_username} onChange={(v) => set("bootstrap_username", v)} placeholder="manager" disabled={disabled} />
          <TextField
            label="Bootstrap secret ref"
            value={draft.bootstrap_secret_ref}
            onChange={(v) => set("bootstrap_secret_ref", v)}
            placeholder="vault:tenants/<slug>/switch/bootstrap#password"
            helper="A vault:<path>#<field> reference — never a plaintext secret."
            error={secretErrors.bootstrap_secret_ref}
            disabled={disabled}
          />
          <TextField label="Bootstrap transport" value={draft.bootstrap_transport} onChange={(v) => set("bootstrap_transport", v)} placeholder="telnet / ssh" disabled={disabled} />
          <NumberField label="Bootstrap port" value={draft.bootstrap_port} onChange={(v) => set("bootstrap_port", v)} min={1} max={65535} disabled={disabled} />
          <TextField label="Mgmt bootstrap host" value={draft.mgmt_bootstrap_host} onChange={(v) => set("mgmt_bootstrap_host", v)} disabled={disabled} />
          <TextField label="Bootstrap mask" value={draft.bootstrap_mask} onChange={(v) => set("bootstrap_mask", v)} disabled={disabled} />
          <TextField label="Bootstrap gateway" value={draft.bootstrap_gateway} onChange={(v) => set("bootstrap_gateway", v)} disabled={disabled} />

          <SectionLabel>Managed (ops) access</SectionLabel>
          <TextField
            label="Ops secret ref"
            value={draft.ops_secret_ref}
            onChange={(v) => set("ops_secret_ref", v)}
            placeholder="vault:tenants/<slug>/switch/ops#password"
            error={secretErrors.ops_secret_ref}
            disabled={disabled}
          />
          <TextField label="Managed host" value={draft.managed_host} onChange={(v) => set("managed_host", v)} disabled={disabled} />
          <TextField label="Managed transport" value={draft.managed_transport} onChange={(v) => set("managed_transport", v)} placeholder="ssh" disabled={disabled} />
          <TextField label="Managed mask" value={draft.managed_mask} onChange={(v) => set("managed_mask", v)} disabled={disabled} />
          <NumberField label="Managed port" value={draft.managed_port} onChange={(v) => set("managed_port", v)} min={1} max={65535} disabled={disabled} />

          <SectionLabel>Migration safety</SectionLabel>
          <SelectField
            label="Provisioning strategy"
            value={draft.provisioning_strategy}
            onChange={(v) => set("provisioning_strategy", v as "" | ProvisioningStrategy)}
            options={STRATEGY_OPTIONS}
            disabled={disabled}
          />
          <CheckboxField label="Preserve until verified" checked={draft.preserve_until_verified} onChange={(v) => set("preserve_until_verified", v)} disabled={disabled} />
          <CheckboxField label="Allow firmware upgrade before backup" checked={draft.allow_firmware_upgrade_before_backup} onChange={(v) => set("allow_firmware_upgrade_before_backup", v)} disabled={disabled} />
          <TextField label="Backup local capture dir" value={draft.backup_local_capture_dir} onChange={(v) => set("backup_local_capture_dir", v)} disabled={disabled} />
          <TextField label="Current interface" value={draft.current_interface} onChange={(v) => set("current_interface", v)} disabled={disabled} />
          <JsonTextarea label="Discovery (JSON)" value={draft.discovery_json} onChange={(v) => set("discovery_json", v)} ok={discovery.ok} error={!discovery.ok ? discovery.error : null} disabled={disabled} />
        </>
      )}

      <StringListField label="Notes" value={draft.notes} onChange={(v) => set("notes", v)} helper="One per line." disabled={disabled} />
      <JsonTextarea label="Vendor extras (JSON, advanced)" value={draft.vendor_extras_json} onChange={(v) => set("vendor_extras_json", v)} ok={vendor.ok} error={!vendor.ok ? vendor.error : null} disabled={disabled} />
    </FormShell>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: "0.78rem", color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: "0.04em", marginTop: 4 }}>
      {children}
    </div>
  );
}

function JsonTextarea({
  label, value, onChange, ok, error, disabled,
}: {
  label: string; value: string; onChange: (v: string) => void; ok: boolean; error: string | null; disabled: boolean;
}) {
  return (
    <div style={{ display: "grid", gap: 6 }}>
      <span style={{ fontSize: "0.8rem", color: "var(--text-dim)" }}>{label}</span>
      <textarea
        value={value}
        disabled={disabled}
        rows={4}
        onChange={(e) => onChange(e.target.value)}
        placeholder={"{}"}
        style={{
          width: "100%", padding: "8px 10px", background: "var(--surface-2)",
          border: `1px solid ${ok ? "var(--border)" : "var(--crit)"}`, borderRadius: 4,
          color: "var(--text)", fontFamily: "monospace", fontSize: "0.85rem", resize: "vertical",
        }}
      />
      {!ok && error && <span style={{ fontSize: "0.75rem", color: "var(--crit)" }}>{error}</span>}
    </div>
  );
}

// ---- helpers ----

type JsonParse = { ok: true; value: Record<string, unknown> } | { ok: false; error: string };

function parseJsonObject(text: string): JsonParse {
  const t = text.trim();
  if (t === "") return { ok: true, value: {} };
  let parsed: unknown;
  try {
    parsed = JSON.parse(t);
  } catch {
    return { ok: false, error: "Not valid JSON." };
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    return { ok: false, error: "Must be a JSON object." };
  }
  return { ok: true, value: parsed as Record<string, unknown> };
}

function toDraft(sw?: SwitchOut): SwitchDraft {
  const ve = sw?.vendor_extras ?? {};
  // vendor_extras_json shows everything EXCEPT management_mode (owned by toggle).
  const veForJson: Record<string, unknown> = { ...ve };
  delete veForJson.management_mode;
  const hasVe = Object.keys(veForJson).length > 0;
  const hasDisc = sw ? Object.keys(sw.discovery ?? {}).length > 0 : false;
  return {
    slug: "",
    management_mode: getManagementMode(ve),
    identity: sw?.identity ?? "",
    model: sw?.model ?? "",
    role: sw?.role ?? "",
    management_vlan_tag: sw?.management_vlan_tag ?? null,
    uplink_port: sw?.uplink_port ?? null,
    maintenance_port: sw?.maintenance_port ?? null,
    target_vlan_name: sw?.target_vlan_name ?? "",
    notes: sw?.notes ?? [],
    vendor_extras_json: hasVe ? JSON.stringify(veForJson, null, 2) : "",
    bootstrap_username: sw?.bootstrap_username ?? "",
    bootstrap_secret_ref: sw?.bootstrap_secret_ref ?? "",
    bootstrap_transport: sw?.bootstrap_transport ?? "",
    bootstrap_port: sw?.bootstrap_port ?? null,
    mgmt_bootstrap_host: sw?.mgmt_bootstrap_host ?? "",
    bootstrap_mask: sw?.bootstrap_mask ?? "",
    bootstrap_gateway: sw?.bootstrap_gateway ?? "",
    ops_secret_ref: sw?.ops_secret_ref ?? "",
    managed_host: sw?.managed_host ?? "",
    managed_transport: sw?.managed_transport ?? "",
    managed_mask: sw?.managed_mask ?? "",
    managed_port: sw?.managed_port ?? null,
    current_interface: sw?.current_interface ?? "",
    provisioning_strategy: sw?.provisioning_strategy ?? "",
    preserve_until_verified: sw?.preserve_until_verified ?? true,
    allow_firmware_upgrade_before_backup: sw?.allow_firmware_upgrade_before_backup ?? false,
    backup_local_capture_dir: sw?.backup_local_capture_dir ?? "",
    discovery_json: hasDisc ? JSON.stringify(sw!.discovery, null, 2) : "",
  };
}

function fromDraft(
  d: SwitchDraft,
  vendorExtras: Record<string, unknown>,
  discovery: Record<string, unknown>,
): SwitchFields {
  const orNull = (s: string) => (s.trim() === "" ? null : s.trim());
  return {
    identity: orNull(d.identity),
    model: orNull(d.model),
    role: orNull(d.role),
    bootstrap_username: orNull(d.bootstrap_username),
    bootstrap_secret_ref: orNull(d.bootstrap_secret_ref),
    bootstrap_transport: orNull(d.bootstrap_transport),
    bootstrap_port: d.bootstrap_port,
    mgmt_bootstrap_host: orNull(d.mgmt_bootstrap_host),
    bootstrap_mask: orNull(d.bootstrap_mask),
    bootstrap_gateway: orNull(d.bootstrap_gateway),
    ops_secret_ref: orNull(d.ops_secret_ref),
    managed_host: orNull(d.managed_host),
    managed_transport: orNull(d.managed_transport),
    managed_mask: orNull(d.managed_mask),
    managed_port: d.managed_port,
    management_vlan_tag: d.management_vlan_tag,
    uplink_port: d.uplink_port,
    maintenance_port: d.maintenance_port,
    current_interface: orNull(d.current_interface),
    target_vlan_name: orNull(d.target_vlan_name),
    provisioning_strategy: d.provisioning_strategy === "" ? null : d.provisioning_strategy,
    preserve_until_verified: d.preserve_until_verified,
    allow_firmware_upgrade_before_backup: d.allow_firmware_upgrade_before_backup,
    backup_local_capture_dir: orNull(d.backup_local_capture_dir),
    discovery,
    // toggle is authoritative — re-apply over whatever the JSON had.
    vendor_extras: withManagementMode(vendorExtras, d.management_mode),
    notes: d.notes,
  };
}
